import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Empty,
  Flex,
  Spin,
  Tag,
  Timeline,
  Image,
  Progress,
} from "antd";
import {
  DownloadOutlined,
  PaperClipOutlined,
  LineChartOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { mockApprovedWorkLines } from "../../data/mockWorkExecution";
import { mockWorkOrders } from "../../data/mockWorkOrders";
import dayjs from "dayjs";

const { Panel } = AntCollapse;

interface ApprovalHistory {
  id: string;
  batchNumber: string;
  stage: string;
  approvedQty: number;
  approvedOn: string;
  approvedBy: string;
  cumulativeAfter: number;
  remarks: string;
}

const WorkExecutionApprovedLineHistory: React.FC = () => {
  const { lineId } = useParams<{ lineId: string }>();
  const navigate = useNavigate();
  const [workLine, setWorkLine] = useState<any>(null);
  const [workOrder, setWorkOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [approvalHistory, setApprovalHistory] = useState<ApprovalHistory[]>([]);

  useEffect(() => {
    if (lineId) {
      loadWorkLineHistory(lineId);
    }
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [lineId]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const loadWorkLineHistory = async (lineId: string) => {
    try {
      setLoading(true);
      const lineData = mockApprovedWorkLines.find(line => line.id === lineId);
      
      if (lineData) {
        setWorkLine(lineData);
        
        // Find related work order
        const woData = mockWorkOrders.find(wo => wo.id === lineData.woId);
        setWorkOrder(woData);
        
        // Mock approval history
        const mockApprovalHistory: ApprovalHistory[] = [
          {
            id: "1",
            batchNumber: "EXB/2025/00075",
            stage: "Final",
            approvedQty: 60,
            approvedOn: "2025-01-08T10:30:00Z",
            approvedBy: "PM - Rajesh",
            cumulativeAfter: 420,
            remarks: "North bay completed"
          },
          {
            id: "2",
            batchNumber: "EXB/2025/00091",
            stage: "Final",
            approvedQty: 80,
            approvedOn: "2025-01-15T14:45:00Z",
            approvedBy: "PM - Rajesh",
            cumulativeAfter: 500,
            remarks: "Work completed"
          }
        ];
        setApprovalHistory(mockApprovalHistory);
      }
    } catch (error) {
      console.error("Error loading work line history:", error);
    } finally {
      setLoading(false);
    }
  };

  // Approval History Table Columns
  const approvalHistoryColumns: TableColumnsType<ApprovalHistory> = [
    {
      title: "Approval Batch #",
      dataIndex: "batchNumber",
      key: "batchNumber",
      width: 150,
      render: (text) => <strong>{text}</strong>
    },
    {
      title: "Stage",
      dataIndex: "stage",
      key: "stage",
      width: 100,
      render: (stage) => (
        <Tag color="green">{stage}</Tag>
      )
    },
    {
      title: "Final Approved Qty",
      dataIndex: "approvedQty",
      key: "approvedQty",
      width: 130,
      align: "right",
      render: (qty) => (
        <strong className="text-success">{qty.toLocaleString()}</strong>
      )
    },
    {
      title: "Approved On",
      dataIndex: "approvedOn",
      key: "approvedOn",
      width: 120,
      render: (date) => dayjs(date).format("DD-MMM-YYYY")
    },
    {
      title: "Approved By",
      dataIndex: "approvedBy",
      key: "approvedBy",
      width: 150
    },
    {
      title: "Cumulative After",
      dataIndex: "cumulativeAfter",
      key: "cumulativeAfter",
      width: 130,
      align: "right",
      render: (qty) => (
        <strong className="text-primary">{qty.toLocaleString()}</strong>
      )
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: (text) => text || <span className="text-muted">---</span>
    }
  ];

  if (loading) {
    return (
      <>
        <Head title="Work Line History" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!workLine) {
    return (
      <>
        <Head title="Work Line Not Found" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Work line not found" />
          </div>
        </Content>
      </>
    );
  }

  const completionPercentage = Math.round((workLine.cumulativeApprovedQty / workLine.contractQuantity) * 100);

  return (
    <>
      <Head title={`Line History - ${workLine.workLineNumber}`} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        {workLine.workLineNumber} | {workLine.workLineTitle}
                      </span>
                      {completionPercentage === 100 && (
                        <Badge color="success" className="ml-2">
                          <CheckCircleOutlined /> Completed
                        </Badge>
                      )}
                    </BlockTitle>
                  </BlockHeadContent>
                  <div className="d-flex">
                    <div className="actions clearfix">
                      <ul className="m-auto d-flex align-items-center justify-content-center p-0" style={{ flexDirection: "row-reverse" }}>
                        <li className="pt-0 pb-0">
                          <Button
                            className="toggle"
                            color="primary"
                            onClick={() => {
                              navigate(`${process.env.PUBLIC_URL}/work-execution-closed-works`);
                            }}
                          >
                            <Icon name="arrow-left" />
                            <span>Back</span>
                          </Button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${
                        sm ? "active" : ""
                      }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <div className="content clearfix">
                <AntCollapse defaultActiveKey={["1", "2", "3", "4"]}>
                  <Panel header="Line Information" key="1">
                    <Col className="justify-start w-100 flex-wrap mt-3 p-3">
                      <div className="w-30 border-end">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Work Line #</div>
                          <div className="data-value w-70">
                            <strong>{workLine.workLineNumber}</strong>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">WO #</div>
                          <div className="data-value w-70">
                            {workLine.woNumber}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Section</div>
                          <div className="data-value w-70">
                            {workLine.section}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Site</div>
                          <div className="data-value w-70">
                            {workLine.site}
                          </div>
                        </div>
                      </div>
                      <div className="w-30 border-end paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Contractor</div>
                          <div className="data-value w-70">
                            {workLine.contractor}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Contract Qty</div>
                          <div className="data-value w-70">
                            <Badge color="outline-primary">
                              {workLine.contractQuantity.toLocaleString()} {workLine.uom}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Approved Qty</div>
                          <div className="data-value w-70">
                            <Badge color="outline-success">
                              {workLine.cumulativeApprovedQty.toLocaleString()} {workLine.uom}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Last Approval</div>
                          <div className="data-value w-70">
                            {dayjs(workLine.lastFinalApproval).format("DD-MMM-YYYY")}
                          </div>
                        </div>
                      </div>
                      <div className="w-30 paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Title</div>
                          <div className="data-value w-70">
                            <strong>{workLine.workLineTitle}</strong>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">% Completion</div>
                          <div className="data-value w-70">
                            <Progress 
                              percent={completionPercentage} 
                              size="small" 
                              strokeColor={completionPercentage === 100 ? "#52c41a" : "#1890ff"}
                            />
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Retention Flag</div>
                          <div className="data-value w-70">
                            {workLine.retentionFlag ? (
                              <Badge color="warning">Applied</Badge>
                            ) : (
                              <Badge color="outline-secondary">No</Badge>
                            )}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Hold Flag</div>
                          <div className="data-value w-70">
                            {workLine.holdFlag ? (
                              <Badge color="danger">Applied</Badge>
                            ) : (
                              <Badge color="outline-secondary">No</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Panel>

                  <Panel header={`Approval History (${approvalHistory.length} batches)`} key="2">
                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 170 + "px",
                          margin: "auto",
                        }}
                      >
                        {approvalHistory.length === 0 ? (
                          <div className="text-center py-5">
                            <Empty description="No approval history found" />
                          </div>
                        ) : (
                          <Table
                            className="customTable"
                            columns={approvalHistoryColumns}
                            dataSource={approvalHistory}
                            scroll={{ x: 1000 }}
                            bordered
                            size="small"
                            pagination={false}
                            rowKey="id"
                            summary={() => (
                              <Table.Summary>
                                <Table.Summary.Row>
                                  <Table.Summary.Cell index={0} colSpan={2}>
                                    <strong>Total Approved</strong>
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={1}>
                                    <strong className="text-success">
                                      {approvalHistory.reduce((sum, history) => sum + history.approvedQty, 0).toLocaleString()}
                                    </strong>
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={2} colSpan={2}>
                                    <strong>Final Cumulative</strong>
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={3}>
                                    <strong className="text-primary">
                                      {workLine.cumulativeApprovedQty.toLocaleString()}
                                    </strong>
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={4}></Table.Summary.Cell>
                                </Table.Summary.Row>
                              </Table.Summary>
                            )}
                          />
                        )}
                      </div>
                    </div>
                  </Panel>

                  <Panel header="Evidence & Documentation" key="3">
                    <div className="mt-3">
                      <h6><strong>Attachments from Approval Batches:</strong></h6>
                      <div className="d-flex flex-wrap">
                        {/* Mock attachments */}
                        <div className="me-3 mb-2">
                          <Badge color="outline-info">
                            <PaperClipOutlined /> Progress_Photos_Jan15.jpg
                            <Button size="sm" color="link" className="p-0 ms-2">
                              <DownloadOutlined />
                            </Button>
                          </Badge>
                        </div>
                        <div className="me-3 mb-2">
                          <Badge color="outline-info">
                            <PaperClipOutlined /> Quality_Check_Report.pdf
                            <Button size="sm" color="link" className="p-0 ms-2">
                              <DownloadOutlined />
                            </Button>
                          </Badge>
                        </div>
                        <div className="me-3 mb-2">
                          <Badge color="outline-info">
                            <PaperClipOutlined /> Measurement_Sheet.xlsx
                            <Button size="sm" color="link" className="p-0 ms-2">
                              <DownloadOutlined />
                            </Button>
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </Panel>

                  <Panel header="Progress Timeline" key="4">
                    <Timeline>
                      {approvalHistory.map((history, index) => (
                        <Timeline.Item 
                          key={history.id}
                          color="green"
                          dot={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                        >
                          <div>
                            <p><strong>{history.batchNumber} - Final Approved</strong></p>
                            <p>Quantity: <strong className="text-success">{history.approvedQty.toLocaleString()} {workLine.uom}</strong></p>
                            <p className="text-muted">
                              {dayjs(history.approvedOn).format("DD MMM YYYY HH:mm")} by {history.approvedBy}
                            </p>
                            <p className="text-muted">
                              Cumulative after approval: <strong>{history.cumulativeAfter.toLocaleString()} {workLine.uom}</strong>
                            </p>
                            {history.remarks && <p className="text-muted">Remarks: {history.remarks}</p>}
                          </div>
                        </Timeline.Item>
                      ))}
                      
                      <Timeline.Item color="blue" dot={<LineChartOutlined style={{ color: '#1890ff' }} />}>
                        <p><strong>Progress Summary</strong></p>
                        <p className="text-muted">
                          Total Progress: <strong>{completionPercentage}%</strong> 
                          ({workLine.cumulativeApprovedQty.toLocaleString()} / {workLine.contractQuantity.toLocaleString()} {workLine.uom})
                        </p>
                        {completionPercentage === 100 && (
                          <Badge color="success">Work Line Completed</Badge>
                        )}
                      </Timeline.Item>
                    </Timeline>
                  </Panel>
                </AntCollapse>
              </div>
            </div>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default WorkExecutionApprovedLineHistory;